import React, { useState, useRef, useEffect, useCallback } from 'react';
import { soundManager } from '../utils/soundUtils';
import styled from 'styled-components';
import { useSessionNavigation } from '../hooks/useSessionNavigation';
import { findValidUser } from '../utils/firebaseUtils';
import card1Clear from '../assets/images/card1-clear.png';
import card1Unclear from '../assets/images/card1-unclear.png';
import card2Clear from '../assets/images/card2-clear.png';
import card2Unclear from '../assets/images/card2-unclear.png';
import card3Clear from '../assets/images/card3-clear.png';
import card3Unclear from '../assets/images/card3-unclear.png';
import card4Clear from '../assets/images/card4-clear.png';
import card4Unclear from '../assets/images/card4-unclear.png';
import card5Clear from '../assets/images/card5-clear.png';
import card5Unclear from '../assets/images/card5-unclear.png';
import card6Clear from '../assets/images/card6-clear.png';
import card6Unclear from '../assets/images/card6-unclear.png';

import bgGuide01 from '../assets/images/cardProgressBG.png';
import cardBg from '../assets/images/card-bg.png';
import emptyBtn from '../assets/images/empty-btn.png';
import titleTxt from '../assets/images/05tale-title-txt.png';
import progressEndPopup1 from '../assets/images/proogress-end-popup1.png';
import progressEndPopup2 from '../assets/images/proogress-end-popup2.png';
import progressEndPopup3 from '../assets/images/proogress-end-popup3.png';
import light1 from '../assets/images/lignt1.png';
import light2 from '../assets/images/lignt2.png';
import light3 from '../assets/images/lignt3.png';
import light4 from '../assets/images/lignt4.png';
import light5 from '../assets/images/lignt5.png';
import light6 from '../assets/images/lignt6.png';

// Bookmark는 순환 참조를 유발하므로 이 컴포넌트 내에 직접 렌더링하지 않음 (Bookmark에서 오버레이로 ProgressCard를 띄움)

const CardContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: fixed;
  top:0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
  background: url(${cardBg}) center/cover no-repeat;
  touch-action: none; /* 모든 터치 동작 방지 */
  z-index: 12000;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.5s ease;
  /* GPU 레이어 분리 및 렌더링 최적화 */
  will-change: opacity;
  transform: translateZ(0); /* GPU 레이어 강제 생성 */
  isolation: isolate; /* 새로운 stacking context 생성 */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3); /* 반투명 검은색 오버레이 */
    transition: opacity 1.5s ease;
    z-index: -1;
  }
`;

const CardsWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !['rotation', 'isDragging'].includes(prop)
})<{ rotation: number; isDragging: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transform: rotateY(${({ rotation }) => rotation}deg) translateY(-0%);
  transition: ${({ isDragging }) => isDragging ? 'none' : 'transform 0.3s ease'};
  transform-style: preserve-3d;
  /* 3D 렌더링 최적화 */
  will-change: transform;
`;

const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => !['index', 'totalCards', 'isActive', 'rotation'].includes(prop)
})<{ 
  index: number; 
  totalCards: number; 
  isActive: boolean;
  rotation: number;
}>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) 
             rotateY(${({ index, totalCards, rotation }) => {
               // 각 카드를 60도 간격으로 배치 (360/6 = 60)
               const angle = (360 / totalCards) * index;
               return angle + rotation;
             }}deg) 
             translateZ(50dvw);
  transition: all 0.3s ease;
  z-index: ${({ isActive }) => isActive ? 20 : 10};
  cursor: pointer;
  /* 카드 렌더링 최적화 */
  will-change: transform;
`;

const CardImage = styled.img.withConfig({
  shouldForwardProp: (prop) => !['isActive', 'isAttempted'].includes(prop)
})<{ isActive: boolean; isAttempted: boolean }>`
  width: auto;
  height: auto;
  max-width: 50dvw;
  border-radius: 12px;
  opacity: 1;
  transition: all 0.3s ease;
  background: transparent;
  mix-blend-mode: multiply;
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 0 0 12px 12px;
  pointer-events: none;
`;

const StatusText = styled.span<{ isCleared: boolean }>`
  font-family: 'MapoDPP', sans-serif;
  font-size: 2.9dvw;
  color: ${({ isCleared }) => isCleared ? '#04FFFF' : '#FFFFFF'};
  position: relative;
  top: -5.6dvw;
  right: -11.5dvw;
  text-align: center;
`;

const CoinText = styled.span`
  font-family: 'MapoDPP', sans-serif;
  font-size: 3.5dvw;
  color: #FEE25F;
  position: relative;
  top: -5.6dvw;
  right: 2.5dvw;
  text-align: center;
`;

const CardWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TitleImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 7%;
  left: 50%;
  transform: translateX(-50%);
  width: 80vw;
  max-width: 400px;
  z-index: 1001;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.5s ease;
`;

const LightImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  height: auto;
  z-index: 5;
  opacity: ${({ visible }) => visible};
  transition: opacity 0.8s ease;
  pointer-events: none;
`;

const NextButton = styled.div`
  position: absolute;
  bottom: 3%;
  left: 50%;
  transform: translateX(-50%);
  width: 65dvw;
  aspect-ratio: 5 / 1; /* 가로:세로 = 5:1 비율 */
  background: url(${emptyBtn}) center/contain no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9999;
  font-family: 'MapoDPP', sans-serif;
  font-size: 4.5dvw;
  color: white;
  pointer-events: auto;
  text-shadow: 0 0 20px #64D3FF;
`;

const BackButton = styled.div`
  position: absolute;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%);
  width: 70dvw;
  aspect-ratio: 5 / 1; /* 가로:세로 = 5:1 비율 */
  background: url(${emptyBtn}) center/contain no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9999;
  font-family: 'MapoDPP', sans-serif;
  font-size: 4.5dvw;
  color: white;
  pointer-events: auto;
  text-shadow: 0 0 20px #64D3FF;
`;

const CompletionPopup = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: ${({ visible }) => visible};
  transition: opacity 0.3s ease;
  cursor: pointer;
`;


const PopupImage = styled.img`
  width: 100vw;
  max-width: 400px;
  height: auto;
  transition: opacity 0.3s ease;
`;

// 상단 조작 버튼은 progress-card에서는 사용하지 않음


interface ProgressCardProps {
  onCardClick?: (cardIndex: number) => void;
  fromBookmark?: boolean; // 북마크에서 온 경우를 구분하는 prop 추가
  onClose?: () => void; // 닫기 콜백 추가
  onOpenBookmark?: () => void; // 북마크 팝업 다시 열기 콜백
}

const ProgressCard: React.FC<ProgressCardProps> = ({ onCardClick, fromBookmark = false, onClose, onOpenBookmark }) => {
  const [visible, setVisible] = useState(0);
  const [activeCard, setActiveCard] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragStarted, setDragStarted] = useState(false);
  const [zoneClearStatus, setZoneClearStatus] = useState<{[key: string]: string}>({});
  const [showCompletionAnimation, setShowCompletionAnimation] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [completionPopupVisible, setCompletionPopupVisible] = useState(0);
  const [completionFadeOut, setCompletionFadeOut] = useState(false);
  const [hideAllElements, setHideAllElements] = useState(false);
  const [currentPopupStep, setCurrentPopupStep] = useState(1); // 현재 팝업 단계 (1, 2, 3)
  const [isPopupTransitioning, setIsPopupTransitioning] = useState(false); // 팝업 전환 중인지 여부
  
  const { navigateWithSession } = useSessionNavigation();
  
  // 디버깅용 로그 추가
  
  // useRef를 사용하여 회전 상태를 관리 (리렌더링 방지)
  const currentRotationRef = useRef(0);
  const cardsRef = useRef<HTMLDivElement>(null);
  
  // 이제 사용하지 않는 rotationCache 제거

  // 카드 데이터 구조 수정 - 클리어/미클리어 이미지 포함
  const cards = [
    { 
      id: 1, 
      clearImage: card1Clear, 
      unclearImage: card1Unclear, 
      zone: 'zone1', 
      nextPage: '/quiz2' 
    },
    { 
      id: 2, 
      clearImage: card2Clear, 
      unclearImage: card2Unclear, 
      zone: 'zone2', 
      nextPage: '/quiz3' 
    },
    { 
      id: 3, 
      clearImage: card3Clear, 
      unclearImage: card3Unclear, 
      zone: 'zone3', 
      nextPage: '/quiz4' 
    },
    { 
      id: 4, 
      clearImage: card4Clear, 
      unclearImage: card4Unclear, 
      zone: 'zone4', 
      nextPage: '/quiz5' 
    },
    { 
      id: 5, 
      clearImage: card5Clear, 
      unclearImage: card5Unclear, 
      zone: 'zone5', 
      nextPage: '/quiz6' 
    },
    { 
      id: 6, 
      clearImage: card6Clear, 
      unclearImage: card6Unclear, 
      zone: 'zone6', 
      nextPage: '/progress' 
    }
  ];

  // 이제 사용하지 않는 getShortestRotation 함수 제거

  // zoneClearStatus 가져오기
  useEffect(() => {
    const fetchZoneStatus = async () => {
      try {
        const currentUser = localStorage.getItem('currentUser');
        if (!currentUser) {
          return;
        }
        
        const userData = JSON.parse(currentUser);
        
        const firebaseUserData = await findValidUser(userData.nickname, userData.avatarNumber);
        
        if (firebaseUserData && firebaseUserData.zoneClearStatus) {
          setZoneClearStatus(firebaseUserData.zoneClearStatus);
        } else {
        }
      } catch (error) {
        console.error('Error fetching zone status:', error);
      }
    };

    fetchZoneStatus();
  }, []);

  // zoneClearStatus가 로드된 후 가장 높은 클리어된 zone의 카드로 초기 설정
  useEffect(() => {
    if (Object.keys(zoneClearStatus).length > 0) {
      const highestClearedZone = getHighestClearedZone();
      if (highestClearedZone > 0) {
        const targetCardIndex = highestClearedZone - 1;
        setActiveCard(targetCardIndex);
        // 회전 각도도 조정
        currentRotationRef.current = -targetCardIndex * 30;
      }
    }
  }, [zoneClearStatus]);

  // 가장 높은 클리어된 zone 찾기
  const getHighestClearedZone = () => {
    
    const entries = Object.entries(zoneClearStatus);
    
    const clearedZones = entries
      .filter(([zone, status]) => {
        return status !== '아직안품';
      })
      .map(([zone, _]) => {
        const zoneNumber = parseInt(zone.replace('zone', ''));
        return zoneNumber;
      });
    
    const result = clearedZones.length > 0 ? Math.max(...clearedZones) : 0;
    
    return result;
  };

  // 현재 활성 카드에 따른 회전 각도 계산 (메모이제이션 적용)
  const getRotation = useCallback(() => {
    // 이제 currentRotationRef.current가 직접 관리되므로 캐시 없이 반환
    return currentRotationRef.current;
  }, []);

  // 페이드인 효과
  useEffect(() => {
    const timer = setTimeout(() => setVisible(1), 100);
    return () => clearTimeout(timer);
  }, []);

  // 활성 카드 변경 시 로그 출력
  useEffect(() => {
  }, [activeCard]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setStartX(e.clientX);
    setStartY(e.clientY);
    setCurrentX(e.clientX);
    setDragStarted(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const newCurrentX = e.clientX;
    const newCurrentY = e.clientY;
    const deltaX = Math.abs(newCurrentX - startX);
    const deltaY = Math.abs(newCurrentY - startY);
    
    // 수평 드래그가 수직 드래그보다 클 때만 드래그 시작 (좌우 드래그 우선)
    if (!dragStarted && deltaX > 30 && deltaX > deltaY) {
      setIsDragging(true);
      setDragStarted(true);
      // 드래그 시작 시 북마크 레이어 고정을 위한 이벤트 발생
      window.dispatchEvent(new CustomEvent('progressCardDragStart'));
    }
    
    if (!isDragging) return;
    
    setCurrentX(newCurrentX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    setDragStarted(false);
    const deltaX = currentX - startX;
    
    // 드래그 종료 시 북마크 레이어 정리를 위한 이벤트 발생
    window.dispatchEvent(new CustomEvent('progressCardDragEnd'));
    
    // 드래그 방향에 따라 한 단계씩 이동
    if (Math.abs(deltaX) > 50) { // 최소 드래그 거리
      if (deltaX > 0) {
        // 오른쪽으로 드래그 - 이전 카드로 (시계방향)
        setActiveCard((prev) => {
          const newActiveCard = (prev - 1 + 6) % 6;
          currentRotationRef.current += 30; // 한 칸씩만 회전
          return newActiveCard;
        });
      } else {
        // 왼쪽으로 드래그 - 다음 카드로 (반시계방향)
        setActiveCard((prev) => {
          const newActiveCard = (prev + 1) % 6;
          currentRotationRef.current -= 30; // 한 칸씩만 회전
          return newActiveCard;
        });
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setStartY(e.touches[0].clientY);
    setCurrentX(e.touches[0].clientX);
    setDragStarted(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const newCurrentX = e.touches[0].clientX;
    const newCurrentY = e.touches[0].clientY;
    const deltaX = Math.abs(newCurrentX - startX);
    const deltaY = Math.abs(newCurrentY - startY);
    
    // 수평 드래그가 수직 드래그보다 클 때만 드래그 시작 (좌우 드래그 우선)
    if (!dragStarted && deltaX > 30 && deltaX > deltaY) {
      setIsDragging(true);
      setDragStarted(true);
      // 드래그 시작 시 북마크 레이어 고정을 위한 이벤트 발생
      window.dispatchEvent(new CustomEvent('progressCardDragStart'));
    }
    
    if (!isDragging) return;
    
    setCurrentX(newCurrentX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    setDragStarted(false);
    const deltaX = currentX - startX;
    
    // 드래그 종료 시 북마크 레이어 정리를 위한 이벤트 발생
    window.dispatchEvent(new CustomEvent('progressCardDragEnd'));
    
    // 드래그 방향에 따라 한 단계씩 이동
    if (Math.abs(deltaX) > 50) { // 최소 드래그 거리
      if (deltaX > 0) {
        // 오른쪽으로 드래그 - 이전 카드로 (시계방향)
        setActiveCard((prev) => {
          const newActiveCard = (prev - 1 + 6) % 6;
          currentRotationRef.current += 30; // 한 칸씩만 회전
          return newActiveCard;
        });
      } else {
        // 왼쪽으로 드래그 - 다음 카드로 (반시계방향)
        setActiveCard((prev) => {
          const newActiveCard = (prev + 1) % 6;
          currentRotationRef.current -= 30; // 한 칸씩만 회전
          return newActiveCard;
        });
      }
    }
  };

  const handleCardClick = (index: number, e: React.MouseEvent) => {
    soundManager.playButtonClick();
    e.stopPropagation(); // 이벤트 버블링 방지
    
    // 드래그가 시작되었으면 클릭 무시
    if (dragStarted) {
      return;
    }
    
    const diff = (index - activeCard + 6) % 6;
    
    if (diff === 1) {
      // 다음 카드로 한 칸 이동
      setActiveCard((prev) => (prev + 1) % 6);
      currentRotationRef.current -= 15;
    } else if (diff === 5) {
      // 이전 카드로 한 칸 이동 (5칸 차이는 1칸 차이와 같음)
      setActiveCard((prev) => (prev - 1 + 6) % 6);
      currentRotationRef.current += 15;
    }
    // 그 외의 경우는 한 칸씩만 이동하므로 무시
  };

  const handleNextButtonClick = () => {
    soundManager.playButtonClick();
    // 아직 안 푼 퀴즈 중에서 가장 숫자가 낮은 퀴즈 찾기
    const nextUnclearedQuiz = getNextUnclearedQuiz();
    if (nextUnclearedQuiz) {
      navigateWithSession(nextUnclearedQuiz);
    }
  };

  // 아직 안 푼 퀴즈 중에서 가장 숫자가 낮은 퀴즈 찾기
  const getNextUnclearedQuiz = () => {
    for (let i = 1; i <= 6; i++) {
      const zoneKey = `zone${i}`;
      if (zoneClearStatus[zoneKey] === '아직안품') {
        // 해당 퀴즈 페이지로 이동
        return `/quiz${i}`; // zone1 -> quiz1, zone2 -> quiz2, ...
      }
    }
    return null; // 모든 퀴즈를 다 푼 경우
  };

  const handleBackButtonClick = () => {
    soundManager.playButtonClick();
    // onClose 콜백이 있으면 호출, 없으면 기존 로직 실행
    if (onClose) {
      onClose();
    } else {
      // ProgressCard에서 돌아온 상태를 localStorage에 저장
      localStorage.setItem('fromProgressCard', 'true');
      navigateWithSession('/tale-house');
    }
  };

  const handleCompletionButtonClick = () => {
    soundManager.playButtonClick();
    // 완료 애니메이션 시작
    setShowCompletionAnimation(true);
    
    // 0.5초 후에 모든 요소들 숨김
    setTimeout(() => {
      setHideAllElements(true);
    }, 500);
    
    // 1.5초 후에 검은색 페이드아웃 시작
    setTimeout(() => {
      setCompletionFadeOut(true);
      
      // 2초 후에 완료 팝업 표시 (1단계부터 시작)
      setTimeout(() => {
        setShowCompletionPopup(true);
        setCompletionPopupVisible(1);
        setCurrentPopupStep(1); // 1단계로 초기화
        setIsPopupTransitioning(false); // 전환 상태 초기화
      }, 2000);
    }, 1500);
  };

  const handleCompletionPopupClick = () => {
    // 3단계인 경우 아무 반응 없음
    if (currentPopupStep === 3) {
      return;
    }
    
    // 이미 전환 중이면 무시
    if (isPopupTransitioning) {
      return;
    }
    
    soundManager.playButtonClick();
    
    // 1단계나 2단계인 경우 페이드아웃 후 다음 단계로
    setIsPopupTransitioning(true);
    setCompletionPopupVisible(0); // 페이드아웃
    
    setTimeout(() => {
      setCurrentPopupStep(prev => prev + 1);
      setCompletionPopupVisible(1); // 페이드인
      setIsPopupTransitioning(false);
    }, 300); // 0.3초 후 다음 팝업 표시
  };

  // progress-card에서는 종료/스킵/북마크 버튼 로직 미사용

  // 북마크는 상단 우측 재사용 컴포넌트로 노출하므로 별도 핸들러 불필요

  const highestClearedZone = getHighestClearedZone();
  
  // 디버깅용 로그

  // 모든 zone이 클리어되었는지 확인
  const allZonesCleared = Object.keys(zoneClearStatus).length === 6 && 
    Object.values(zoneClearStatus).every(status => status !== '아직안품');

  return (
    <CardContainer
      visible={showCompletionAnimation && !completionFadeOut ? 0 : visible}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <TitleImage src={titleTxt} alt="제목" visible={hideAllElements ? 0 : visible} />
      

      
      {/* progress-card에서는 종료/스킵/북마크 버튼 미표시 */}
      
      {/* Light 이미지들 - 클리어된 zone에만 표시 */}
      <LightImage src={light1} alt="Light 1" visible={hideAllElements ? 0 : (activeCard === 0 && zoneClearStatus.zone1 !== '아직안품' ? 1 : 0)} />
      <LightImage src={light2} alt="Light 2" visible={hideAllElements ? 0 : (activeCard === 1 && zoneClearStatus.zone2 !== '아직안품' ? 1 : 0)} />
      <LightImage src={light3} alt="Light 3" visible={hideAllElements ? 0 : (activeCard === 2 && zoneClearStatus.zone3 !== '아직안품' ? 1 : 0)} />
      <LightImage src={light4} alt="Light 4" visible={hideAllElements ? 0 : (activeCard === 3 && zoneClearStatus.zone4 !== '아직안품' ? 1 : 0)} />
      <LightImage src={light5} alt="Light 5" visible={hideAllElements ? 0 : (activeCard === 4 && zoneClearStatus.zone5 !== '아직안품' ? 1 : 0)} />
      <LightImage src={light6} alt="Light 6" visible={hideAllElements ? 0 : (activeCard === 5 && zoneClearStatus.zone6 !== '아직안품' ? 1 : 0)} />
      
      {(() => {
        const currentRotation = getRotation();
        return (
          <>
            {!hideAllElements && (
              <CardsWrapper rotation={currentRotation} isDragging={isDragging}>
                {cards.map((card, index) => {
                  const zoneStatus = zoneClearStatus[`zone${card.id}`];
                  const isCleared = zoneStatus === '성공';
                  const isFailed = zoneStatus === '실패';
                  const isNotAttempted = zoneStatus === '아직안품';
                  const isAttempted = zoneStatus === '성공' || zoneStatus === '실패';
                  
                  const cardImage = isAttempted ? card.clearImage : card.unclearImage;
                  const statusText = isCleared ? 'CLEAR' : 'FAIL';
                  const coinAmount = isCleared ? '100' : '50';
                  
                  // 아직 안 푼 퀴즈인지 확인
                  const displayStatusText = isNotAttempted ? '???' : statusText;
                  const displayCoinAmount = isNotAttempted ? '???' : coinAmount;
                  
                  return (
                    <Card
                      key={card.id}
                      index={index}
                      totalCards={6}
                      isActive={index === activeCard}
                      rotation={currentRotation}
                      onClick={(e) => handleCardClick(index, e)}
                    >
                      <CardWrapper>
                        <CardImage
                          src={cardImage}
                          alt={`카드 ${card.id}`}
                          isActive={index === activeCard}
                          isAttempted={isAttempted}
                        />
                        <CardOverlay>
                          <StatusText isCleared={isCleared}>
                            {displayStatusText}
                          </StatusText>
                          <CoinText>
                            {displayCoinAmount}
                          </CoinText>
                        </CardOverlay>
                      </CardWrapper>
                    </Card>
                  );
                })}
              </CardsWrapper>
            )}
            {/* 버튼 표시 로직 */}
            {(() => {
              // 북마크에서 온 경우 항상 돌아가기 버튼 표시
              if (fromBookmark && !hideAllElements) {
                return (
                  <BackButton onClick={handleBackButtonClick}>
                    돌아가기
                  </BackButton>
                );
              }
              
              // 모든 zone이 클리어된 경우 어떤 카드든 완료 버튼 표시
              if (!fromBookmark && allZonesCleared && !hideAllElements) {
                return (
                  <NextButton onClick={handleCompletionButtonClick}>
                    완료
                  </NextButton>
                );
              }
              
              // 모든 카드에 다음 버튼 표시
              if (!fromBookmark && !hideAllElements) {
                return (
                  <NextButton onClick={handleNextButtonClick}>
                    다음
                  </NextButton>
                );
              }
              
              return null;
            })()}
          </>
        );
      })()}
      
      {/* 완료 팝업 */}
      {showCompletionPopup && (
        <CompletionPopup 
          visible={completionPopupVisible}
          onClick={isPopupTransitioning || currentPopupStep === 3 ? undefined : handleCompletionPopupClick}
          style={{ pointerEvents: isPopupTransitioning ? 'none' : 'auto' }}
        >
          <PopupImage 
            src={
              currentPopupStep === 1 ? progressEndPopup1 :
              currentPopupStep === 2 ? progressEndPopup2 :
              progressEndPopup3
            } 
            alt={`완료 팝업 ${currentPopupStep}`} 
          />
        </CompletionPopup>
      )}


      {/* 종료/스킵 팝업 없음 */}
    </CardContainer>
  );
};

export default ProgressCard; 